import React, { useRef, useEffect, useMemo } from "react";
import * as d3 from "d3";

interface DataPoint {
  label: string;
  value: number;
}

interface LineChartProps {
  data: DataPoint[];
  numOfLines?: number;
  width?: number;
  height?: number;
}

const formatNumber = (num: number) => {
  if (num >= 1e9) return (num / 1e9).toFixed(1) + "B";
  if (num >= 1e6) return (num / 1e6).toFixed(1) + "M";
  if (num >= 1e3) return (num / 1e3).toFixed(1) + "K";
  return num;
};

const LineChart: React.FC<LineChartProps> = ({
  data,
  numOfLines = 5,
  width = 500,
  height = 300,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const maxValue = useMemo(() => {
    return Math.max(...data.map((d) => d.value));
  }, [data]);

  const yAxisMaxValue = useMemo(() => {
    return maxValue * 1.25;
  }, [maxValue]);

  const yAxisTicks = useMemo(() => {
    const ticks = [];
    const interval = yAxisMaxValue / numOfLines;
    for (let i = 0; i <= numOfLines; i++) {
      ticks.push(interval * i);
    }
    return ticks;
  }, [yAxisMaxValue, numOfLines]);

  const needsHorizontalScroll = data.length > 12;
  const minGroupWidth = 120; 
  const calculatedChartWidth = needsHorizontalScroll 
      ? Math.max(data.length * minGroupWidth, width)
      : width;
    useEffect(() => {
        const wrapper = wrapperRef.current;

        const resizeObserver = new ResizeObserver((entries) => {
            if (entries[0]) {
                const newWidth = entries[0].contentRect.width;
                setContainerWidth(newWidth);
            }
        });

        if (wrapper) {
            resizeObserver.observe(wrapper);
        }

        return () => {
            if (wrapper) {
                resizeObserver.unobserve(wrapper);
            }
        };
    }, []);
  useEffect(() => {
    const svg = d3.select(svgRef.current);

    svg.selectAll("*").remove();

    const margin = { top: 20, right: 20, bottom: 70, left: 40 };
    const barWidth = calculatedChartWidth - margin.left - margin.right;
    const barHeight = height - margin.top - margin.bottom;

    const mousemove = (event: any, d: any) => {

      const xPosition = event.offsetX + margin.left - 25;
      const yPosition = event.offsetY + margin.top - 40;
      const text = `${d.label} - ${d.value}`;
      tooltip
        .attr("x", xPosition)
        .attr("y", yPosition)
        .style("opacity", 9)
        .style("height", text.length >= 16 ? 50 : 35);

      tooltipText.html(text);
    };

    const mouseleave = () => {
      tooltip.style("opacity", 0);
    };

    const x = d3
      .scaleBand()
      .domain(data.map((d: any) => d.label))
      .range([0, barWidth])
      .padding(0.1);

    const y = d3
      .scaleLinear()
      .domain([0, d3.max(data, (d: any) => d.value)])
      .range([barHeight, 0]);

    const chart = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const line = d3
      .line<DataPoint>()
      //@ts-expect-error not applicable
      .x((d: any) => x(d.label) + x.bandwidth() / 2)
      .y((d: any) => y(d.value));

    chart
      .append("path")
      .datum(data)
      .attr("class", "line")
      .attr("d", line)
      .style("fill", "none")
      .style("stroke", "#357AF6")
      .style("stroke-width", 2);

    chart
      .selectAll(".point")
      .data(data)
      .enter()
      .append("circle")
      .attr("class", "point")
      //@ts-expect-error not applicable
      .attr("cx", (d: any) => x(d.label) + x.bandwidth() / 2)
      .attr("cy", (d: any) => y(d.value))
      .attr("r", 4)
      .style("fill", "#357AF6")
      .on("mousemove", mousemove)
      .on("mouseleave", mouseleave);

    yAxisTicks.slice(1).forEach((tick) => {
      chart
        .append("line")
        .attr("class", "dotted-line")
        .attr("x1", 0)
        .attr("y1", y(tick))
        .attr("x2", width)
        .attr("y2", y(tick))
        .style("stroke", "gray")
        .style("stroke-dasharray", "3,3");
    });

    const xAxis = chart
      .append("g")
      .attr("class", "x-axis")
      .attr("transform", `translate(0, ${barHeight})`)
      .call(d3.axisBottom(x));

    xAxis.selectAll(".tick line").remove();

    const yAxis = chart
      .append("g")
      .attr("class", "y-axis")
      .call(
        d3
          .axisLeft(y)
          .tickValues(yAxisTicks)
          //@ts-expect-error not applicable
          .tickFormat((d: any) => {
            return formatNumber(d);
          })
          .tickSizeOuter(0),
        0,
      );

    yAxis.selectAll(".domain").remove();
    yAxis.selectAll(".tick line").remove();

    const tooltip = svg
      .append("foreignObject")
      .attr("class", "tooltip")
      .attr("width", 120)
      .attr("height", 20)
      .style("opacity", 0)
      .style("position", "absolute")
      .style("background-color", "white")
      .style("border", "none")
      .style("box-shadow", "0rem 0rem 0.469rem 0rem rgba(0, 0, 0, 0.15)")
      .style("border-radius", "0.313rem")
      .style("padding", "0.625rem")
      .style("font-size", "0.75rem");

    const tooltipText = tooltip
      .append("xhtml:div")
      .style("font-size", "0.75rem")
      .style("color", "black");
  }, [data, height, width, yAxisMaxValue, yAxisTicks, calculatedChartWidth]);

  return (
    <div ref={wrapperRef} style={{ width: '100%', height: '100%' }}>
        <svg 
            ref={svgRef}
            width={calculatedChartWidth}
            height={height}
            viewBox={`0 0 ${calculatedChartWidth} ${height}`}
        />
    </div>
  );
};

export default LineChart;
